import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Shield, Zap, Users } from 'lucide-react';

const AgentCommunicationPage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-4 py-20">
        {/* Back Button */}
        <Link
          to="/self-phone"
          className="inline-flex items-center text-indigo-400 hover:text-indigo-300 transition-colors mb-12"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to SELF Phone
        </Link>

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
            Real-Time Push-to-Talk for Humans and Agents
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Instant, secure communication between users and AI agents with advanced encryption and real-time execution.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-indigo-950/30 p-8 rounded-2xl backdrop-blur-sm border border-indigo-500/20"
          >
            <MessageSquare className="w-12 h-12 text-indigo-400 mb-4" />
            <h3 className="text-xl font-semibold mb-4">Secure Messaging</h3>
            <p className="text-gray-300">
              Agent-to-agent encrypted messaging system with end-to-end protection for all communications.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-indigo-950/30 p-8 rounded-2xl backdrop-blur-sm border border-indigo-500/20"
          >
            <Shield className="w-12 h-12 text-indigo-400 mb-4" />
            <h3 className="text-xl font-semibold mb-4">Built-in Redundancy</h3>
            <p className="text-gray-300">
              Multiple communication paths using satellite signal and local mesh networks for reliable connectivity.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-indigo-950/30 p-8 rounded-2xl backdrop-blur-sm border border-indigo-500/20"
          >
            <Zap className="w-12 h-12 text-indigo-400 mb-4" />
            <h3 className="text-xl font-semibold mb-4">Instant Execution</h3>
            <p className="text-gray-300">
              Push-to-talk functionality for instant communication. Real-time response and task execution.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-indigo-950/30 p-8 rounded-2xl backdrop-blur-sm border border-indigo-500/20"
          >
            <Users className="w-12 h-12 text-indigo-400 mb-4" />
            <h3 className="text-xl font-semibold mb-4">Team Collaboration</h3>
            <p className="text-gray-300">
              Enable dynamic team actions and AI agent collaborations through secure group channels.
            </p>
          </motion.div>
        </div>

        {/* Additional Info */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-indigo-950/30 p-8 rounded-2xl backdrop-blur-sm border border-indigo-500/20">
            <h3 className="text-2xl font-semibold mb-6 text-center">Communication Features</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-indigo-400 rounded-full mr-3"></span>
                  Real-time Voice Transmission
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-indigo-400 rounded-full mr-3"></span>
                  Encrypted Text Messaging
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-indigo-400 rounded-full mr-3"></span>
                  Group Channel Support
                </li>
              </ul>
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-indigo-400 rounded-full mr-3"></span>
                  Task Signal Broadcasting
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-indigo-400 rounded-full mr-3"></span>
                  Offline Message Queue
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-indigo-400 rounded-full mr-3"></span>
                  Priority Signal Routing
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentCommunicationPage; 